import { FormEvent, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { Betragsfeld, Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, toast } from '@elbklar/ui';

import { TextField } from '@/components/Felder';
import { loansApi } from '@/lib/api';
import { betragFehler } from '@/lib/betrag';
import { datum, ganzzahl, hatFehler, hoechstens, nichtVor, ohne, pflicht, unlesbareFelder, zumErstenFehler, type Fehler } from '@/lib/pruefen';
import { heuteIso } from '@/lib/format';
import type { Loan } from '@/types';

interface Props {
  offen: boolean;
  onSchliessen: () => void;
  onGespeichert: () => void;
  /** Gesetzt = Konditionen korrigieren, leer = neues Darlehen. */
  kredit?: Loan | null;
}

// Summe, Zins und Rate sind Decimal-Texte aus dem Betragsfeld, `null` solange
// dort keine Zahl steht.
const LEER = {
  name: '',
  summe: '' as string | null,
  zins: '' as string | null,
  laufzeit: '',
  rate: '' as string | null,
  auszahlung: heuteIso(),
  ersteRate: '',
  tilgungsfrei: '0',
};

type Feld = keyof typeof LEER;

/** Was `absenden` aus den drei Betragsfeldern gelesen hat, als Decimal-Text. */
interface Konditionen {
  summe: string;
  zins: string;
  rate: string;
}

function ausKredit(kredit: Loan) {
  return {
    name: kredit.name,
    summe: kredit.principal as string | null,
    zins: kredit.interest_rate as string | null,
    laufzeit: String(kredit.term_months),
    rate: kredit.monthly_payment as string | null,
    auszahlung: kredit.disbursement_date,
    ersteRate: kredit.first_payment_date,
    tilgungsfrei: String(kredit.grace_months),
  };
}

/**
 * Anlegen und Korrigieren in einem Dialog.
 *
 * Das Korrigieren ist der wichtigere Fall: bis die Hausbank steht, sind die
 * hinterlegten Konditionen Plandaten. Sie zu aendern darf nicht bedeuten, das
 * Darlehen zu loeschen und neu anzulegen — dabei ginge die Historie verloren.
 * Restschuld und Tilgungsplan werden ohnehin aus den Konditionen gerechnet,
 * eine Korrektur wirkt also sofort auf den ganzen Plan.
 */
export function LoanDialog({ offen, onSchliessen, onGespeichert, kredit }: Props) {
  const [formular, setFormular] = useState(LEER);
  const [fehler, setFehler] = useState<Fehler<Feld>>({});

  const bearbeiten = Boolean(kredit);

  useEffect(() => {
    if (!offen) return;
    setFormular(kredit ? ausKredit(kredit) : LEER);
    setFehler({});
  }, [offen, kredit]);

  /**
   * Wert setzen und den Fehler des Felds zurücknehmen. Hängt ein Fehler an
   * einem zweiten Feld (erste Rate an der Auszahlung, tilgungsfreie Zeit an
   * der Laufzeit), geht er mit.
   */
  const feld = (name: Feld, wert: string | null) => {
    setFormular({ ...formular, [name]: wert });
    setFehler((f) => {
      const ohneFeld = ohne(f, name);
      if (name === 'auszahlung') return ohne(ohneFeld, 'ersteRate');
      if (name === 'laufzeit') return ohne(ohneFeld, 'tilgungsfrei');
      return ohneFeld;
    });
  };

  const speichern = useMutation({
    // Decimal-Texte aus den Betragsfeldern, von `absenden` geprüft. Der Zins
    // hat vier Stellen (3,875 %), das Betragsfeld rundet ihn nicht auf Cent.
    mutationFn: ({ summe, zins, rate }: Konditionen) => {
      const nutzdaten = {
        name: formular.name.trim(),
        principal: summe,
        interest_rate: zins,
        term_months: Number(formular.laufzeit),
        monthly_payment: rate,
        disbursement_date: formular.auszahlung,
        first_payment_date: formular.ersteRate || formular.auszahlung,
        grace_months: Number(formular.tilgungsfrei || 0),
      };

      return kredit
        ? loansApi.update(kredit.id, nutzdaten)
        : loansApi.create(nutzdaten);
    },
    onSuccess: () => {
      toast.success(bearbeiten ? 'Konditionen geändert' : 'Darlehen angelegt');
      if (!bearbeiten) setFormular(LEER);
      onGespeichert();
      onSchliessen();
    },
    onError: () =>
      toast.error('Speichern fehlgeschlagen — stimmen Laufzeit und Rate?'),
  });

  // Grenzen aus LoanCreate (finance-api): Bezeichnung 1–255 Zeichen, Summe
  // und Rate über 0, Zins 0–100 %, Laufzeit 1–600 Monate, tilgungsfreie Zeit
  // ab 0 und kürzer als die Laufzeit, erste Rate nicht vor der Auszahlung.
  const absenden = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const unlesbar = unlesbareFelder(event.currentTarget);
    const { summe, zins, rate } = formular;
    const laufzeitFehler = ganzzahl(formular.laufzeit, {
      min: 1,
      max: 600,
      pflicht: true,
      unlesbar: unlesbar.has('laufzeit'),
    });
    // Leer heißt keine tilgungsfreie Zeit. Mit der Laufzeit vergleichen geht
    // erst, wenn die stimmt.
    const tilgungsfreiFehler =
      ganzzahl(formular.tilgungsfrei, { min: 0, unlesbar: unlesbar.has('tilgungsfrei') }) ??
      (!laufzeitFehler && Number(formular.tilgungsfrei || 0) >= Number(formular.laufzeit)
        ? 'Weniger Monate als die Laufzeit'
        : undefined);
    const neu: Fehler<Feld> = {
      name: pflicht(formular.name, 'Bezeichnung fehlt') ?? hoechstens(formular.name, 255),
      // Ein leerer Zins heißt null Prozent; Summe und Rate müssen dastehen.
      summe: betragFehler(summe, { nichtNull: true }),
      zins: betragFehler(zins, { leerErlaubt: true }) ?? (Number(zins) > 100 ? 'Höchstens 100 %' : undefined),
      laufzeit: laufzeitFehler,
      rate: betragFehler(rate, { nichtNull: true }),
      auszahlung: datum(formular.auszahlung, { unlesbar: unlesbar.has('auszahlung') }),
      ersteRate:
        datum(formular.ersteRate, { leerErlaubt: true, unlesbar: unlesbar.has('ersteRate') }) ??
        nichtVor(formular.ersteRate, formular.auszahlung, 'Nicht vor der Auszahlung'),
      tilgungsfrei: tilgungsfreiFehler,
    };
    setFehler(neu);
    if (hatFehler(neu) || summe === null || zins === null || rate === null) {
      zumErstenFehler(event.currentTarget);
      return;
    }
    speichern.mutate({ summe, zins: zins || '0', rate });
  };

  return (
    <Dialog open={offen} onOpenChange={(o) => !o && onSchliessen()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{bearbeiten ? 'Konditionen bearbeiten' : 'Darlehen anlegen'}</DialogTitle>
        </DialogHeader>
        <form noValidate onSubmit={absenden} className="space-y-4">
          <TextField
            label="Bezeichnung"
            placeholder="z. B. Betriebsmittelkredit 2026"
            value={formular.name}
            onChange={(e) => feld('name', e.target.value)}
            error={fehler.name}
            required
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Betragsfeld
              label="Darlehenssumme (€)"
              placeholder="190000"
              hinweis={(wert) => (wert === null ? 'Tausenderpunkt und Komma sind erlaubt: 190.000,50' : undefined)}
              error={fehler.summe}
              value={formular.summe ?? ''}
              onChange={(wert) => feld('summe', wert)}
              required
            />
            <Betragsfeld
              label="Nominalzins (% p. a.)"
              placeholder="4,25"
              einheit="prozent"
              nachkommastellen={4}
              error={fehler.zins}
              value={formular.zins ?? ''}
              onChange={(wert) => feld('zins', wert)}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label="Laufzeit (Monate)"
              type="number"
              name="laufzeit"
              min={1}
              max={600}
              placeholder="84"
              value={formular.laufzeit}
              onChange={(e) => feld('laufzeit', e.target.value)}
              error={fehler.laufzeit}
              required
            />
            <Betragsfeld
              label="Monatsrate (€)"
              placeholder="2630"
              error={fehler.rate}
              value={formular.rate ?? ''}
              onChange={(wert) => feld('rate', wert)}
              required
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label="Auszahlung am"
              type="date"
              name="auszahlung"
              value={formular.auszahlung}
              onChange={(e) => feld('auszahlung', e.target.value)}
              error={fehler.auszahlung}
              required
            />
            <TextField
              label="Erste Rate am"
              type="date"
              name="ersteRate"
              hint="Leer: am Tag der Auszahlung"
              value={formular.ersteRate}
              onChange={(e) => feld('ersteRate', e.target.value)}
              error={fehler.ersteRate}
            />
          </div>

          <TextField
            label="Tilgungsfreie Monate"
            type="number"
            name="tilgungsfrei"
            min={0}
            hint="In dieser Zeit fallen nur Zinsen an"
            value={formular.tilgungsfrei}
            onChange={(e) => feld('tilgungsfrei', e.target.value)}
            error={fehler.tilgungsfrei}
          />

          {bearbeiten && (
            <p className="rounded-lg bg-muted px-3 py-2 t-small text-muted-foreground">
              Restschuld und Tilgungsplan werden aus den Konditionen gerechnet,
              nicht gespeichert — die Korrektur wirkt sofort auf den gesamten
              Plan und auf die Projektion.
            </p>
          )}

          <DialogFooter className="pt-2">
            <Button variant="ghost" onClick={onSchliessen}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" loading={speichern.isPending}>
              {bearbeiten ? 'Konditionen speichern' : 'Darlehen anlegen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
